import { createContext, ReactElement, useContext } from 'react';
import { useMsal } from '@azure/msal-react';
import axios, { AxiosInstance } from 'axios';

import { useUserAuth } from '../auth/user-auth-provider';
import { getAuthToken } from '../azure/msal/get-auth-token';

const AxiosContext = createContext<AxiosInstance | null>(null);

export type AxiosProviderProps = {
  children: ReactElement;
  baseUrl: string;
  scopes: string[];
};

export const AxiosApiProvider = ({ baseUrl, scopes, children }: AxiosProviderProps) => {
  const { instance } = useMsal();
  const axiosInstance = axios.create({
    baseURL: baseUrl,
  });
  const { token, expiry, updateToken } = useUserAuth();

  axiosInstance.interceptors.request.use(
    async (config) => {
      if (!token || !expiry || expiry < new Date(Date.now())) {
        const { accessToken, expiry } = await getAuthToken(instance, scopes);
        updateToken && updateToken(accessToken, expiry);
        config.headers['Authorization'] = 'Bearer ' + accessToken;
      } else {
        config.headers['Authorization'] = 'Bearer ' + token;
      }
      return config;
    },
    (error) => {
      Promise.reject(error);
    },
  );

  return <AxiosContext.Provider value={axiosInstance}>{children}</AxiosContext.Provider>;
};

export const useAxiosApi = () => {
  const context = useContext(AxiosContext);

  if (!context) {
    throw new Error('useAppConfig must be used within a AxiosApiProvider');
  }

  return context;
};
